import { Empty, Flex, Group, Image } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { ViewType } from '@lighthouse/core'
import { type ActorEvent, type BlockType, DELAY_TYPE, EVENT_TYPE, TIMER_INTERVAL_UNIT, TIMER_TRIGGER_MODE } from '@lighthouse/core'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { ActionAdderProvider } from '@/contexts/ActionAdderContext'

import { EventActions } from './EventActions'
import type { EventAdderProps } from './EventAdder'
import { EventAdder } from './EventAdder'

const SCxContainer = styled.div`
    border-radius: 8px;
    margin: 8px;
    padding: 8px;
    border: 1px dashed var(--color-gray-200);
    background-color: var(--color-gray-25);
`
interface EventListProps extends Pick<EventAdderProps, 'field'> {
    label?: string
    blockType: BlockType | 'page'
    value: ActorEvent[]
    view?: {
        viewType: ViewType
        viewDsId: string
    }
    form?: {
        formDsId: string
    }
    isImage?: boolean
    style?: React.CSSProperties
    onChange: (value: ActorEvent[]) => void
}

export const EventList: React.FC<EventListProps> = ({ label = '动作', view, form, isImage, blockType, field, value, style, onChange }) => {
    const handleDelete = useCallback(
        (id: string) => {
            const newEventList = value.filter(v => v.id !== id)
            onChange(newEventList)
        },
        [onChange, value]
    )

    const handleAddEvent = useCallback(
        (eventType: EVENT_TYPE) => {
            const scrollBottomConfig =
                eventType === EVENT_TYPE.SCROLL_BOTTOM
                    ? {
                          bottom: 0
                      }
                    : undefined
            const timerConfig =
                eventType === EVENT_TYPE.TIMER
                    ? {
                          triggerMode: TIMER_TRIGGER_MODE.ONCE,
                          intervalPreset: '5s' as const,
                          customInterval: 1,
                          customIntervalUnit: TIMER_INTERVAL_UNIT.SECOND
                      }
                    : undefined
            const newEvent: ActorEvent = {
                id: nanoid(),
                type: eventType,
                actions: [],
                config: {
                    ...scrollBottomConfig,
                    ...timerConfig,
                    delayRunConfig: {
                        type: DELAY_TYPE.NONE
                    }
                }
            }
            if (value) {
                onChange([...value, newEvent])
                return
            }
            onChange([newEvent])
        },
        [onChange, value]
    )

    const handleEventChange = useCallback(
        (event: ActorEvent) => {
            const newEventList = value.map(v => {
                if (v.id === event.id) {
                    return event
                }
                return v
            })
            onChange(newEventList)
        },
        [onChange, value]
    )

    const addedEventTypes = useMemo(() => value?.map(v => v.type), [value])

    return (
        <Group
            style={style}
            label={label}
            extra={<EventAdder blockType={blockType} field={field} addedEventTypes={addedEventTypes} addEvent={handleAddEvent} />}
        >
            <Flex direction="column" gap={8}>
                {!value || value.length === 0 ? (
                    <SCxContainer>
                        <Empty
                            icon={<Image width={48} src={getAssetUrl('empty', 'empty_animation.png')} />}
                            styles={{
                                root: {
                                    height: 130
                                },
                                wrapper: {
                                    gap: 12
                                }
                            }}
                            description="当事件发生时执行动作"
                        />
                    </SCxContainer>
                ) : (
                    value.map(actorEvent => {
                        if (actorEvent.type === EVENT_TYPE.RECORD_CLICK) {
                            return (
                                <ActionAdderProvider key={actorEvent.id} viewDsId={view?.viewDsId} viewType={view?.viewType}>
                                    <EventActions actorEvent={actorEvent} onDelete={handleDelete} onChange={handleEventChange} />
                                </ActionAdderProvider>
                            )
                        }
                        if (actorEvent.type === EVENT_TYPE.SUBMIT) {
                            return (
                                <ActionAdderProvider key={actorEvent.id} formDsId={form?.formDsId}>
                                    <EventActions actorEvent={actorEvent} onDelete={handleDelete} onChange={handleEventChange} />
                                </ActionAdderProvider>
                            )
                        }
                        if (isImage && actorEvent.type === EVENT_TYPE.CLICK) {
                            return (
                                <ActionAdderProvider key={actorEvent.id} isImage>
                                    <EventActions
                                        key={actorEvent.id}
                                        actorEvent={actorEvent}
                                        onDelete={handleDelete}
                                        onChange={handleEventChange}
                                    />
                                </ActionAdderProvider>
                            )
                        }
                        return (
                            <EventActions
                                key={actorEvent.id}
                                actorEvent={actorEvent}
                                onDelete={handleDelete}
                                onChange={handleEventChange}
                            />
                        )
                    })
                )}
            </Flex>
        </Group>
    )
}
